import React from 'react';
import { CheckIcon, XIcon, ShirtIcon, SparklesIcon } from 'lucide-react';
const acceptable = [{
  item: 'Dress Shirts & Blouses',
  note: 'Button-downs, silk tops, fitted blouses'
}, {
  item: 'Tailored Trousers & Slacks',
  note: 'Well-fitted dress pants, chinos'
}, {
  item: 'Blazers & Sport Coats',
  note: 'Structured jackets, suit separates'
}, {
  item: 'Cocktail Dresses',
  note: 'Evening wear, midi & maxi dresses'
}, {
  item: 'Dress Shoes & Heels',
  note: 'Loafers, oxfords, stilettos, dress boots'
}, {
  item: 'Designer Denim',
  note: 'Dark wash, fitted — no rips or distressing'
}, {
  item: 'Jumpsuits & Rompers',
  note: 'Elegant, well-tailored pieces'
}, {
  item: 'Cultural & Traditional Attire',
  note: 'Agbada, Dashiki, Ankara, Kente — always welcome'
}];
const notAcceptable = [{
  item: 'Athletic Wear',
  note: 'Gym shorts, joggers, sweatpants, tracksuits'
}, {
  item: 'Hats & Durags',
  note: 'Baseball caps, beanies, durags, visors'
}, {
  item: 'Work Boots & Timberlands',
  note: 'Construction boots, steel-toe footwear'
}, {
  item: 'Flip Flops & Slides',
  note: 'Open-toe casual sandals, pool slides'
}, {
  item: 'Ripped or Distressed Jeans',
  note: 'Heavily torn, bleached, or baggy denim'
}, {
  item: 'Oversized or Graphic Tees',
  note: 'Band tees, logo-heavy shirts, jerseys'
}, {
  item: 'Tank Tops & Sleeveless Shirts',
  note: 'For men — sleeveless undershirts, muscle tees'
}, {
  item: 'Shorts',
  note: 'Cargo shorts, basketball shorts, board shorts'
}];
export function DressCode() {
  return <main className="bg-matte min-h-screen" data-id="element-129">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden" data-id="element-130">
        <div className="absolute inset-0 z-0" style={{
        backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.95)), url('https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} data-id="element-131" />
        <div className="relative z-10 text-center px-6 mt-16 fade-section" data-id="element-132">
          <ShirtIcon className="w-12 h-12 text-gold mx-auto mb-6" data-id="element-133" />
          <h1 className="font-serif text-[50px] md:text-[62px] lg:text-[74px] text-white mb-6" data-id="element-134">
            DRESS CODE
          </h1>
          <div className="w-24 h-px bg-gold mx-auto mb-6" data-id="element-135" />
          <p className="font-sans text-lg md:text-xl text-white/80 max-w-2xl mx-auto tracking-wide" data-id="element-136">
            Tempoo is a sophisticated dining and lounge experience. We ask that
            all guests dress accordingly.
          </p>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-6 fade-section" data-id="element-137">
        <div className="max-w-3xl mx-auto text-center" data-id="element-138">
          <SparklesIcon className="w-8 h-8 text-gold mx-auto mb-6" data-id="element-139" />
          <p className="font-sans text-lg text-white/70 leading-relaxed" data-id="element-140">
            At Tempoo, the way you present yourself is part of the experience.
            Our dress code ensures every guest contributes to the elevated
            atmosphere that makes our lounge special. When you look good, you
            feel good — and that energy is contagious.
          </p>
        </div>
      </section>

      {/* Two Column: Acceptable / Not Acceptable */}
      <section className="py-28 px-6 fade-section" data-id="element-141">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-142">
          {/* Acceptable */}
          <div className="bg-charcoal border border-gold/20 p-8 md:p-12" data-id="element-143">
            <div className="flex items-center gap-4 mb-8" data-id="element-144">
              <div className="w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center" data-id="element-145">
                <CheckIcon className="w-5 h-5 text-gold" data-id="element-146" />
              </div>
              <h2 className="font-serif text-[26px] md:text-[32px] text-white" data-id="element-147">
                Acceptable
              </h2>
            </div>
            <p className="font-sans text-base text-white/50 mb-8" data-id="element-148">
              We encourage sophisticated, stylish attire that matches the Tempoo
              atmosphere.
            </p>
            <ul className="space-y-6" data-id="element-149">
              {acceptable.map((entry, i) => <li key={i} className="flex items-start gap-4" data-id="element-150">
                  <CheckIcon className="w-5 h-5 text-gold flex-shrink-0 mt-1" data-id="element-151" />
                  <div data-id="element-152">
                    <h4 className="font-sans text-base text-white font-medium" data-id="element-153">
                      {entry.item}
                    </h4>
                    <p className="font-sans text-sm text-white/40 mt-1" data-id="element-154">
                      {entry.note}
                    </p>
                  </div>
                </li>)}
            </ul>
          </div>

          {/* Not Acceptable */}
          <div className="bg-charcoal border border-white/5 p-8 md:p-12" data-id="element-155">
            <div className="flex items-center gap-4 mb-8" data-id="element-156">
              <div className="w-10 h-10 bg-red-900/20 border border-red-800/30 flex items-center justify-center" data-id="element-157">
                <XIcon className="w-5 h-5 text-red-400" data-id="element-158" />
              </div>
              <h2 className="font-serif text-[26px] md:text-[32px] text-white" data-id="element-159">
                Not Permitted
              </h2>
            </div>
            <p className="font-sans text-base text-white/50 mb-8" data-id="element-160">
              The following items are not permitted inside Tempoo. Management
              reserves the right to deny entry.
            </p>
            <ul className="space-y-6" data-id="element-161">
              {notAcceptable.map((entry, i) => <li key={i} className="flex items-start gap-4" data-id="element-162">
                  <XIcon className="w-5 h-5 text-red-400/70 flex-shrink-0 mt-1" data-id="element-163" />
                  <div data-id="element-164">
                    <h4 className="font-sans text-base text-white font-medium" data-id="element-165">
                      {entry.item}
                    </h4>
                    <p className="font-sans text-sm text-white/40 mt-1" data-id="element-166">
                      {entry.note}
                    </p>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Bottom Note */}
      <section className="pb-28 px-6 fade-section" data-id="element-167">
        <div className="max-w-3xl mx-auto text-center" data-id="element-168">
          <div className="w-16 h-px bg-gold mx-auto mb-8" data-id="element-169" />
          <p className="font-sans text-base text-white/50 leading-relaxed mb-4" data-id="element-170">
            Management reserves the right to refuse entry at their discretion.
            If you're unsure about your outfit, err on the side of overdressing
            — you'll fit right in.
          </p>
          <p className="font-sans text-lg text-gold tracking-wide" data-id="element-171">
            When in doubt, dress up.
          </p>
        </div>
      </section>
    </main>;
}